// src/Science.jsx
import React, { useLayoutEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import SiteNav from "./SiteNav";
import { Link } from "react-router-dom";
import ScienceIntake from "./ScienceIntake";
import ScienceMTC from "./ScienceMTC";
import ForScienceDope from "./ForScienceDope";

gsap.registerPlugin(ScrollTrigger);

export default function Science() {
  const rootRef = useRef(null);
  const mtcRef = useRef(null);
const intakeRef = useRef(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      // ---- HERO ENTRANCE ----
      const hero = document.querySelector(".sci-hero");
      if (hero) {
        gsap.set(".sci-hero .hline", { y: 40, autoAlpha: 0, rotateX: 15, transformOrigin: "50% 100%" });
        gsap.set([".sci-hero .hero-kicker", ".sci-hero .hero-sub"], { y: 20, autoAlpha: 0 });
        gsap.set(".sci-shine", { xPercent: -120, opacity: 0 });
        gsap.set(".sci-hero-img", { scale: 1.06, xPercent: -3, autoAlpha: 0 });

        const tl = gsap.timeline({ defaults: { ease: "power3.out" } });
        tl.to(".sci-hero-img", { autoAlpha: 1, scale: 1, xPercent: 0, duration: 0.9 })
          .to(".sci-hero .hero-kicker", { y: 0, autoAlpha: 1, duration: 0.5 }, "-=0.55")
          .to(".sci-hero .hline", { y: 0, autoAlpha: 1, rotateX: 0, duration: 0.6, stagger: 0.08 }, "-=0.35")
          .to(".sci-hero .hero-sub", { y: 0, autoAlpha: 1, duration: 0.55 }, "-=0.25")
          .to(".sci-shine", { xPercent: 120, opacity: 1, duration: 1.2, ease: "power2.out" }, "-=0.1");

        // Parallax on scroll (very gentle)
        gsap.to(".sci-hero-img", {
          yPercent: -10,
          ease: "none",
          scrollTrigger: { trigger: hero, start: "top top", end: "bottom top", scrub: true }
        });
      }

      // ---- ABSORPTION BARS ----
      gsap.set(".abs-bar .fill", { width: "0%" });
      ScrollTrigger.batch(".abs-row", {
        start: "top 85%",
        onEnter: (batch) => {
          batch.forEach((row) => {
            const target = row.querySelector(".fill");
            const to = row.getAttribute("data-to") || "70";
            gsap.to(target, { width: to + "%", duration: 1.1, ease: "power2.out" });
          });
        }
      });

      // ---- CARDS REVEAL ----
      gsap.utils.toArray(".sci-card").forEach((el, i) => {
        gsap.from(el, {
          y: 24,
          autoAlpha: 0,
          duration: 0.55,
          ease: "power3.out",
          delay: i * 0.06,
          scrollTrigger: { trigger: el, start: "top 90%" }
        });
      });

      // ---- TIMELINE STEPS ----
      gsap.utils.toArray(".gt-step").forEach((step, i) => {
        gsap.from(step, {
          y: 30,
          autoAlpha: 0,
          duration: 0.55,
          ease: "power3.out",
          scrollTrigger: { trigger: step, start: "top 88%" }
        });
        const dot = step.querySelector(".dot");
        if (dot) {
          gsap.fromTo(
            dot,
            { scale: 0 },
            {
              scale: 1,
              duration: 0.5,
              ease: "back.out(2)",
              scrollTrigger: { trigger: step, start: "top 85%" }
            }
          );
        }
      });

            // SCIENCE sections
        [mtcRef.current, intakeRef.current].forEach((sec) => {
        if (!sec) return;
        const q = gsap.utils.selector(sec);

        // header
        gsap.from(q(".sci-head > *"), {
            y: 24, autoAlpha: 0, duration: 0.6, ease: "power3.out", stagger: 0.06,
            scrollTrigger: { trigger: sec, start: "top 85%", once: true }
        });

        // paragraphs & list items
        gsap.from(q(".sci-copy p, .sci-copy li"), {
            y: 16, autoAlpha: 0, duration: 0.5, ease: "power3.out", stagger: 0.05,
            scrollTrigger: { trigger: q(".sci-copy"), start: "top 85%", once: true }
        });

        // cards on the side
        gsap.from(q(".sci-aside .card"), {
            y: 18, autoAlpha: 0, scale: 0.98, duration: 0.5, ease: "power3.out", stagger: 0.08,
            scrollTrigger: { trigger: q(".sci-aside"), start: "top 90%", once: true }
        });

        // gentle parallax on images
        q("img.parlx, .sci-bg.parlx").forEach((img) => {
            gsap.to(img, {
            yPercent: -10,
            ease: "none",
            scrollTrigger: { trigger: sec, start: "top bottom", end: "bottom top", scrub: true }
            });
        });
        });


      // Safe refresh after assets load
      const onLoad = () => ScrollTrigger.refresh();
      window.addEventListener("load", onLoad);
      gsap.delayedCall(0, () => ScrollTrigger.refresh());
    }, rootRef);

    return () => ctx.revert();
  }, []);

  return (
    <div className="page science" ref={rootRef}>

    
      {/* HERO 
      <section className="sci-hero" aria-label="Science landing">
        <div className="sci-hero-media">
          {/* Replace with your image path 
          <img className="sci-hero-img" src="/DSCF0379.jpg" alt="Athlete fueling, motion blur" />
          <span className="sci-grain" aria-hidden />
        </div>
        <div className="sci-hero-panel">
          <p className="hero-kicker">The Science</p>
          <h1 className="hero-title" aria-label="Fuel built on physiology, not hype">
            <span className="hline">BUILT ON SCIENCE.</span> 
            <span className="hline">PROVEN ON </span>
            <span className="hline">THE BIKE.</span>
          </h1>
          <p className="hero-sub">
            Fuel built on physiology, not hype.<br/>
            Every ride starts before you clip in. What you put in your body decides how far and how hard you can go.<br/>
            Dope Shot was born from decades of racing, trial, and real-world testing. It is fuel made by athletes who know what endurance really feels like.
          </p>
          
        </div>
        <span className="sci-shine" aria-hidden />
      </section>*/}



      <SiteNav />
      <ForScienceDope />
      {/*<ScienceIntake />
      <ScienceMTC />*/}



      {/* ===== MTC / Composition ===== 
<section id="science-mtc" className="sci-mtc" ref={mtcRef} aria-labelledby="mtc-title">
  <div className="sci-wrap">
    <header className="sci-head">
      <p className="sci-kicker">The blend</p>
      <h2 id="mtc-title" className="sci-title">Multiple Transportable Carbohydrates (MTC)</h2>
      <p className="sci-sub">Why our four-carb system is easier to absorb, gentler on your gut.</p>
    </header>

    <div className="sci-grid">
      <article className="sci-copy">
        <p>
          Made up of <strong>Maltodextrin, Glucose (Dextrose), Fructose, Sucrose</strong>, plus sea salt
          and citric acid. These four carbohydrates together — referred to as <strong>MTC</strong> (multiple
          transportable carbohydrates) — have shown a <strong>20–50% increase in exogenous carbohydrate
          oxidation</strong> versus consumption of single carbohydrate sources. MTCs have also been shown to
          <strong> decrease gastrointestinal distress</strong> versus a single carbohydrate source.
        </p>

        <h3 className="sci-h3">Why not a 1:1 or 1:0.8 glucose:fructose ratio?</h3>
        <p>
          The research on exact ratios is, in my opinion, non-conclusive — and those mixes are often so sweet
          that easily consuming <strong>100 g/hr</strong> can be literally breathtaking for many athletes (including me).
          These ratios also ignore the <strong>Dextrose Equivalent (DE)</strong> of different maltodextrins.
        </p>
        <p>
          <em>Dextrose</em> is the technical term for glucose. <em>Dextrose Equivalent (DE)</em> is the degree of hydrolysis
          (breakdown) of starch — so maltodextrin (though a complex carb) has a varying percentage of glucose equivalents.
          We choose a <strong>high-DE maltodextrin</strong> that behaves more like an MTC than a single carb.
        </p>
        <p>
          When we mix our maltodextrin with glucose, fructose, and sucrose, we believe we get the “holy grail” of sports
          nutrition: <strong>an MTC profile with a high glucose percentage, high solubility, and low sweetness</strong>.
          That’s the secret to getting calories in with minimal GI issues — and without the drink being too sweet to sip on hot days.
        </p>

        <h3 className="sci-h3">Our philosophy</h3>
        <p>
          At Dope Shot, we build around <strong>trusted research proven year after year</strong>. MTCs and
          <strong> 100 g/hr intake</strong> have repeatedly shown results — and Dope Shot is the easiest way to achieve both.
        </p>
      </article>

      {/* right column visuals 
      <aside className="sci-aside" aria-label="Ingredients and model">
        <div className="card c-ingredients">
          <h4>Ingredients</h4>
          <ul className="chips">
            <li>Maltodextrin (high DE)</li>
            <li>Glucose</li>
            <li>Fructose</li>
            <li>Sucrose</li>
            <li>Sea Salt</li>
            <li>Citric Acid</li>
          </ul>
        </div>

        <div className="card c-figure">
          <img className="parlx" src="/about-us1.png" alt="Dope Shot mixing/serving" loading="lazy" />
          <p className="figcap">High-calorie, gut-friendly fuel — designed for steady intake.</p>
        </div>
      </aside>
    </div>
  </div>

  {/* subtle background image (optional) 
  <img className="sci-bg parlx" src="/DSCF9560.JPG" alt="" aria-hidden />
</section>

{/* ===== Intake Guidance ===== 
<section id="science-intake" className="sci-intake" ref={intakeRef} aria-labelledby="intake-title">
  <div className="sci-wrap">
    <header className="sci-head">
      <p className="sci-kicker">Beyond race day</p>
      <h2 id="intake-title" className="sci-title">Carbohydrate Intake Outside Events</h2>
      <p className="sci-sub">How daily fueling, pre-workout, and post-workout timing help performance.</p>
    </header>

    <div className="sci-grid">
      <article className="sci-copy">
        <ol className="sci-list">
          <li>
            <strong>Daily use for training readiness:</strong> Don’t run on empty. Train how you intend to perform —
            stay topped up. Using Dope Shot daily helps your gut get familiar with the product, making ingested
            carbohydrates easier to use as energy (exogenous carbohydrate oxidation).
          </li>
          <li>
            <strong>Pre-workout priming:</strong> A couple shots (100–200 calories) before exercise can positively
            impact performance and feel easy on your stomach — especially when nerves run high.
          </li>
          <li>
            <strong>Post-workout window:</strong> In the <em>30–60 minute glycogen window</em>, your body more easily
            absorbs fuel. This speeds recovery for tomorrow’s session. You can keep using Dope Shot post-workout, but
            I also like to mix in juice, soda, or sports drinks. Dope Shot is one tool in your nutrition toolbox.
          </li>
          <li>
            <strong>Gut training matters:</strong> Product formulation is only half the story — consistent use builds
            the physiological adaptation that lets you comfortably reach higher carb intakes. That’s why pros integrate
            gut training into their programs for peak performance.
          </li>
        </ol>
      </article>

      <aside className="sci-aside">
        <div className="card c-quote">
          <blockquote>
            “MTC + gut training = the simplest path to 100 g/hr without the crash.”
          </blockquote>
          <span className="sig">— Dope Shot</span>
        </div>

        <div className="card c-image">
          <img className="parlx" src="/IMG_6888.JPG" alt="Training ride in Southern California" loading="lazy" />
        </div>
      </aside>
    </div>
  </div>
</section> */}

      
      
      {/* THREE CARDS: PILLARS 
      <section className="sci-pills" aria-label="Fuel pillars">
        <div className="sci-card">
          <h3>Multi-Carb Blend</h3>
          <p>
            Maltodextrin, glucose, fructose, and sucrose engage different intestinal transporters so you can
            utilize more carbohydrate per hour than single-carb fuels.
          </p>
        </div>
        <div className="sci-card">
          <h3>Absorption Ceiling</h3>
          <p>
            Trained athletes typically top out around <strong>~120 g/hr</strong> total carbohydrate oxidation. DOPESHOT
            is designed for a smooth <strong>~100 g/hr</strong> from the bottle—easy to sip, easy to absorb.
          </p>
        </div>
        <div className="sci-card">
          <h3>Gut-Friendly by Design</h3>
          <p>
            Balanced osmolality and clean flavor keep the stomach calm at race pace. Train your intake and your gut
            adapts in 8–10 weeks.
          </p>
        </div>
      </section>




      {/* ABSORPTION VISUAL
      <section className="absorption" aria-label="Absorption visualization">
        <header className="abs-head">
          <h2 className="abs-title">Parallel Pathways. Higher Throughput.</h2>
          <p className="abs-sub">
            Different transporters (SGLT1/GLUT5) move different sugars. Combining them raises the ceiling.
          </p>
        </header>

        <div className="abs-rows">
          <div className="abs-row" data-to="65">
            <div className="label">Maltodextrin Only</div>
            <div className="abs-bar"><span className="fill"></span></div>
            <div className="val">~65 g/hr</div>
          </div>

          <div className="abs-row" data-to="100">
            <div className="label">DOPESHOT Bottle</div>
            <div className="abs-bar"><span className="fill"></span></div>
            <div className="val">~100 g/hr</div>
          </div>

          <div className="abs-row" data-to="120">
            <div className="label">Trained Upper Limit*</div>
            <div className="abs-bar"><span className="fill"></span></div>
            <div className="val">~120 g/hr</div>
          </div>
        </div>

        <p className="abs-note">*Most research suggests diminishing returns beyond ~120 g/hr.</p>
      </section>

      {/* GUT TRAINING TIMELINE 
      <section className="gut-train" aria-label="Gut training">
        <header className="gt-head">
          <h2 className="gt-title">Gut Training: Build Your Hourly Intake</h2>
          <p className="gt-sub">
            Start where you are. Increase by ~5 g/hr each week until you hit your comfortable max (often 80–120 g/hr).
          </p>
        </header>

        <div className="gt-steps">
          <article className="gt-step">
            <span className="dot" aria-hidden />
            <h4>Weeks 1–2</h4>
            <p>Begin at ~50 g/hr. Small, steady sips. Log comfort.</p>
          </article>
          <article className="gt-step">
            <span className="dot" aria-hidden />
            <h4>Weeks 3–6</h4>
            <p>Increase by ~5 g/hr weekly. Hold steady if you feel distress.</p>
          </article>
          <article className="gt-step">
            <span className="dot" aria-hidden />
            <h4>Weeks 7–10</h4>
            <p>Approach ~100 g/hr. Many athletes find this the sweet spot.</p>
          </article>
          <article className="gt-step">
            <span className="dot" aria-hidden />
            <h4>Ceiling</h4>
            <p>Well-trained athletes may reach ~120 g/hr using mixed carbs.</p>
          </article>
        </div>
      </section>*/}
      {/* Footer */}
      <footer className="site-footer">
        <div className="foot-inner">
          <Link to="/">Terms</Link>
          <Link to="/">Privacy</Link>
          <Link to="/">Support</Link>
        </div>
        <div className="foot-copy">© Dope Shot</div>
      </footer>
    </div>
    
  );
}
